import { css } from '@rxdi/lit-html';

export const SteelFabTheme = css`
  /* =============================================
     SteelFab Design Tokens — all colors, sizes,
     and effects are defined here as CSS variables.
     Change any value to retheme the entire site.
   ============================================= */

  /* Primary brand palette */
  :host {
    --sf-accent: #f2994a;
    --sf-accent-dark: #d9822b;
    --sf-accent-light: #f6b26b;
    --sf-navy: #5578a8;
    --sf-navy-dark: #4a48b0;

    --sf-navy-light: #222066;
    --sf-steel: #6b8ec5;
    --sf-steel-light: #a3bfe0;
    --sf-steel-dark: #5578a8;
    --sf-dark: #2e3a5e;
    --sf-dark-mid: #344270;

    /* Backgrounds */
    --sf-bg-primary: #ffffff;
    --sf-bg-secondary: #f4f7fc;
    --sf-bg-tertiary: #edf2fa;
    --sf-bg-dark: var(--sf-dark);
    --sf-bg-input: #f7f9fc;

    /* Text */
    --sf-text-primary: #2d3748;
    --sf-text-secondary: #556677;
    --sf-text-muted: #8494a7;
    --sf-text-light: #ffffff;
    --sf-text-steel: var(--sf-steel-light);

    /* Borders */
    --sf-border-light: #e2e8f0;
    --sf-border-medium: #cbd5e0;
    --sf-border-focus: var(--sf-steel);

    /* Shadows */
    --sf-shadow-sm: 0 1px 3px rgba(42, 40, 120, 0.06);
    --sf-shadow-md: 0 4px 12px rgba(42, 40, 120, 0.08);
    --sf-shadow-lg: 0 8px 30px rgba(42, 40, 120, 0.1);
    --sf-shadow-xl: 0 20px 60px rgba(42, 40, 120, 0.14);
    --sf-shadow-card: 0 4px 24px rgba(42, 40, 120, 0.07);

    /* Border radius */
    --sf-radius-sm: 3px;
    --sf-radius-md: 3px;
    --sf-radius-lg: 10px;
    --sf-radius-xl: 13px;
    --sf-radius-pill: 9999px;

    /* Typography */
    --sf-font-sans: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
    --sf-font-size-xs: 0.75rem;
    --sf-font-size-sm: 0.875rem;
    --sf-font-size-base: 1rem;
    --sf-font-size-lg: 1.125rem;
    --sf-font-size-xl: 1.25rem;
    --sf-font-size-2xl: 1.5rem;
    --sf-font-size-3xl: 1.875rem;
    --sf-font-size-4xl: 2.5rem;
    --sf-font-size-5xl: 3.5rem;

    /* Gradients */
    --sf-gradient-primary: linear-gradient(135deg, var(--sf-navy) 0%, var(--sf-navy-light) 100%);
    --sf-gradient-hero: linear-gradient(135deg, var(--sf-dark) 0%, var(--sf-navy) 50%, var(--sf-navy-light) 100%);
    --sf-gradient-accent: linear-gradient(135deg, var(--sf-accent-dark) 0%, var(--sf-accent) 100%);
    --sf-gradient-text: linear-gradient(135deg, var(--sf-navy) 0%, var(--sf-steel) 100%);

    /* Keep backward compat with existing --primary-color consumers */
    --primary-color: var(--sf-navy);
    --primary-dark: var(--sf-navy-dark);
    --secondary-color: var(--sf-steel);
    --text-primary: var(--sf-text-primary);
    --text-secondary: var(--sf-text-secondary);
    --text-muted: var(--sf-text-muted);
    --bg-primary: var(--sf-bg-primary);
    --bg-secondary: var(--sf-bg-secondary);
    --bg-tertiary: var(--sf-bg-tertiary);
    --border-light: var(--sf-border-light);
    --border-medium: var(--sf-border-medium);
    --border-default: var(--sf-border-light);
    --shadow-sm: var(--sf-shadow-sm);
    --shadow-md: var(--sf-shadow-md);
    --shadow-lg: var(--sf-shadow-lg);
    --shadow-xl: var(--sf-shadow-xl);
    --radius-sm: var(--sf-radius-sm);
    --radius-md: var(--sf-radius-md);
    --radius-lg: var(--sf-radius-lg);
    --radius-xl: var(--sf-radius-xl);
  }

  /* =============================================
     Layout Utilities
   ============================================= */
  .section {
    padding: 60px 0;
    position: relative;
  }

  .section-dark {
    background: var(--sf-bg-dark);
    color: var(--sf-text-light);
  }

  .section-light {
    background: var(--sf-bg-secondary);
  }

  .section-title {
    font-size: var(--sf-font-size-4xl);
    font-weight: 700;
    color: var(--sf-text-primary);
    text-align: center;
    margin-bottom: 1rem;
    line-height: 1.2;
    font-family: var(--sf-font-sans);
  }

  .section-title-light {
    color: var(--sf-text-light);
  }

  .section-subtitle {
    font-size: var(--sf-font-size-xl);
    color: var(--sf-text-secondary);
    text-align: center;
    margin-bottom: 3rem;
    max-width: 600px;
    margin-left: auto;
    margin-right: auto;
    line-height: 1.6;
  }

  .section-subtitle-light {
    color: var(--sf-text-steel);
  }

  .container {
    max-width: 100%;
    padding: 0 2rem;
    margin: 0 1.5rem 0 1.5rem;
  }

  .section-header {
    text-align: center;
    margin-bottom: 3rem;
  }

  .section-badge {
    display: inline-block;
    background: var(--sf-bg-secondary);
    color: var(--sf-navy);
    border: 1px solid var(--sf-bg-tertiary);
    font-size: var(--sf-font-size-sm);
    font-weight: 600;
    padding: 6px 16px;
    border-radius: var(--sf-radius-pill);
    margin-bottom: 1rem;
    letter-spacing: 0.05em;
    text-transform: uppercase;
  }

  /* =============================================
     Buttons
   ============================================= */
  .btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 0.75rem 1.75rem;
    font-size: var(--sf-font-size-base);
    font-weight: 600;
    font-family: var(--sf-font-sans);
    text-decoration: none;
    border-radius: var(--sf-radius-md);
    border: none;
    cursor: pointer;
    transition: all 0.25s ease;
    text-align: center;
    letter-spacing: 0.01em;
  }

  .btn-primary {
    background: var(--sf-gradient-accent);
    color: var(--sf-text-light);
    box-shadow: 0 4px 15px rgb(0 0 0 / 22%);
  }

  .btn-primary:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(107, 142, 197, 0.5);
    filter: brightness(1.05);
  }

  .btn-secondary {
    background: transparent;
    color: var(--sf-text-light);
    border: 2px solid rgba(255, 255, 255, 0.6);
  }

  .btn-secondary:hover {
    background: rgba(255, 255, 255, 0.1);
    border-color: white;
    transform: translateY(-2px);
  }

  .btn-outline {
    background: transparent;
    color: var(--sf-navy);
    border: 2px solid var(--sf-navy);
  }

  .btn-outline:hover {
    background: var(--sf-navy);
    color: white;
    transform: translateY(-2px);
  }

  .btn-danger {
    background: #e53e3e;
    color: white;
  }

  .btn-danger:hover {
    background: #c53030;
    transform: translateY(-1px);
  }

  .btn-sm {
    padding: 0.5rem 1rem;
    font-size: var(--sf-font-size-sm);
  }
  .btn-lg {
    padding: 1rem 2.5rem;
    font-size: var(--sf-font-size-lg);
  }

  /* =============================================
     Cards
   ============================================= */
  .card {
    background: var(--sf-bg-primary);
    border-radius: var(--sf-radius-lg);
    padding: 2rem;
    box-shadow: var(--sf-shadow-card);
    border: 1px solid var(--sf-border-light);
    transition: all 0.3s ease;
  }

  .card:hover {
    transform: translateY(-4px);
    box-shadow: var(--sf-shadow-xl);
    border-color: var(--sf-steel);
  }

  .card-title {
    font-size: var(--sf-font-size-2xl);
    font-weight: 600;
    color: var(--sf-text-primary);
    margin-bottom: 1rem;
    font-family: var(--sf-font-sans);
  }

  .card-description {
    color: var(--sf-text-secondary);
    line-height: 1.7;
    margin-bottom: 1.5rem;
  }

  /* Category card */
  .category-card {
    text-align: center;
    cursor: pointer;
    position: relative;
    overflow: hidden;
  }

  .category-card::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: var(--sf-gradient-accent);
    transform: scaleX(0);
    transition: transform 0.3s ease;
  }

  .category-card:hover::before {
    transform: scaleX(1);
  }

  .category-icon {
    width: 72px;
    height: 72px;
    margin: 0 auto 1.25rem;
    background: var(--sf-bg-secondary);
    border-radius: var(--sf-radius-xl);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 2rem;
    border: 1px solid var(--sf-border-light);
    transition: all 0.3s ease;
  }

  .category-card:hover .category-icon {
    background: var(--sf-gradient-primary);
    border-color: transparent;
    transform: scale(1.05);
  }

  /* Product card */
  .product-card {
    cursor: pointer;
    overflow: hidden;
  }

  .product-card-image {
    width: 100%;
    height: 220px;
    background: linear-gradient(145deg, var(--sf-bg-secondary) 0%, var(--sf-bg-tertiary) 100%);
    border-radius: var(--sf-radius-md);
    margin-bottom: 1.25rem;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    border: 1px solid var(--sf-border-light);
    transition: all 0.3s ease;
    position: relative;
  }

  .product-card-image img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.4s ease;
  }

  .product-card:hover .product-card-image {
    border-color: var(--sf-steel);
  }
  .product-card:hover .product-card-image img {
    transform: scale(1.05);
  }

  .product-badge {
    position: absolute;
    top: 12px;
    left: 12px;
    background: var(--sf-gradient-accent);
    color: white;
    font-size: var(--sf-font-size-xs);
    font-weight: 700;
    padding: 4px 10px;
    border-radius: var(--sf-radius-pill);
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }

  /* =============================================
     Grids
   ============================================= */
  .grid {
    display: grid;
    gap: 2rem;
  }
  .grid-2 {
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  }
  .grid-3 {
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  }
  .grid-4 {
    grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  }

  /* =============================================
     Forms
   ============================================= */
  .form-group {
    margin-bottom: 1.25rem;
  }

  .form-label {
    display: block;
    font-size: var(--sf-font-size-sm);
    font-weight: 600;
    color: var(--sf-text-primary);
    margin-bottom: 0.5rem;
  }

  .form-control {
    width: 100%;
    padding: 0.75rem 1rem;
    font-size: var(--sf-font-size-base);
    font-family: var(--sf-font-sans);
    color: var(--sf-text-primary);
    background: var(--sf-bg-input);
    border: 1.5px solid var(--sf-border-light);
    border-radius: var(--sf-radius-md);
    transition: border-color 0.2s ease, box-shadow 0.2s ease;
    box-sizing: border-box;
  }

  .form-control:focus {
    outline: none;
    border-color: var(--sf-border-focus);
    box-shadow: 0 0 0 3px rgba(107, 142, 197, 0.2);
  }

  textarea.form-control {
    resize: vertical;
    min-height: 120px;
  }

  /* =============================================
     Table
   ============================================= */
  .sf-table {
    width: 100%;
    border-collapse: collapse;
    font-size: var(--sf-font-size-sm);
    overflow-y: scroll;
  }

  .sf-table th {
    text-align: left;
    padding: 0.75rem 1rem;
    font-weight: 600;
    color: var(--sf-text-muted);
    border-bottom: 2px solid var(--sf-border-light);
    background: var(--sf-bg-secondary);
    text-transform: uppercase;
    letter-spacing: 0.05em;
    font-size: var(--sf-font-size-xs);
  }

  .sf-table td {
    padding: 0.875rem 1rem;
    border-bottom: 1px solid var(--sf-border-light);
    color: var(--sf-text-primary);
    vertical-align: middle;
  }

  .sf-table tr:hover td {
    background: var(--sf-bg-secondary);
  }

  /* =============================================
     Utilities
   ============================================= */
  .text-gradient {
    background: var(--sf-gradient-text);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }

  .flex {
    display: flex;
  }
  .flex-col {
    flex-direction: column;
  }
  .items-center {
    align-items: center;
  }
  .justify-center {
    justify-content: center;
  }
  .justify-between {
    justify-content: space-between;
  }
  .gap-4 {
    gap: 1rem;
  }
  .gap-6 {
    gap: 1.5rem;
  }
  .gap-8 {
    gap: 2rem;
  }
  .text-center {
    text-align: center;
  }
  .font-bold {
    font-weight: 700;
  }
  .font-semibold {
    font-weight: 600;
  }
  .mb-2 {
    margin-bottom: 0.5rem;
  }
  .mb-4 {
    margin-bottom: 1rem;
  }
  .mb-6 {
    margin-bottom: 1.5rem;
  }
  .mb-8 {
    margin-bottom: 2rem;
  }
  .mt-4 {
    margin-top: 1rem;
  }
  .mt-6 {
    margin-top: 1.5rem;
  }
  .mt-8 {
    margin-top: 2rem;
  }

  /* =============================================
     Animations
   ============================================= */
  @keyframes fadeInUp {
    from {
      opacity: 0;
      transform: translateY(24px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @keyframes fadeInDown {
    from {
      opacity: 0;
      transform: translateY(-24px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }

  @keyframes countUp {
    from {
      opacity: 0;
      transform: translateY(10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  .animate-fadeInUp {
    animation: fadeInUp 0.6s ease both;
  }
  .animate-fadeInDown {
    animation: fadeInDown 0.6s ease both;
  }
  .animate-fadeIn {
    animation: fadeIn 0.6s ease both;
  }
  .delay-1 {
    animation-delay: 0.1s;
  }
  .delay-2 {
    animation-delay: 0.2s;
  }
  .delay-3 {
    animation-delay: 0.3s;
  }
  .delay-4 {
    animation-delay: 0.4s;
  }
  .delay-5 {
    animation-delay: 0.5s;
  }
  .delay-6 {
    animation-delay: 0.6s;
  }

  /* =============================================
     Responsive
   ============================================= */
  @media (max-width: 768px) {
    .container {
      padding: 0;
    }
    .section {
      padding: 60px 0;
    }
    .section-title {
      font-size: var(--sf-font-size-3xl);
    }
    .section-subtitle {
      font-size: var(--sf-font-size-lg);
    }
    .grid-2,
    .grid-3,
    .grid-4 {
      grid-template-columns: 1fr;
    }
  }

  @media (max-width: 480px) {
    .section-title {
      font-size: var(--sf-font-size-2xl);
    }
    .card {
      padding: 1.5rem;
    }
    .btn-lg {
      padding: 0.875rem 1.75rem;
      width: 100%;
    }
  }
`;

/** @deprecated use SteelFabTheme */
export const ReactivumTheme = SteelFabTheme;
